import axios from 'axios';
import { api } from '../../../services/api';
import { ICAService, RevokeClientInviteResponse } from '../contracts/ca.api.contracts';
import { CAServiceError } from './serviceErrors';
import {
  normalizeAssignment,
  normalizeAuditLogEvent,
  normalizeCAClientDetail,
  normalizeCAClientListItem,
  normalizeDashboardAnalytics,
  normalizeDocumentRequest,
  normalizeDocumentFolderNode,
  normalizeDocumentWorkflowRequirement,
  normalizeEngagementState,
  normalizeMapping,
  normalizeTask,
  normalizeStaffMember,
} from './caSchemaAdapter';

type ApiEnvelope<T> = {
  data?: T;
};

type RawPaginated = { items?: unknown[]; total?: number; page?: number; limit?: number };

const unwrapData = <T>(payload: unknown): T => {
  const envelope = payload as ApiEnvelope<T> | T;
  if (envelope && typeof envelope === 'object' && 'data' in (envelope as ApiEnvelope<T>)) {
    return (envelope as ApiEnvelope<T>).data as T;
  }
  return envelope as T;
};

const resolveClientOrganizationId = (input: { clientOrganizationId?: string; clientOrgId?: string }): string =>
  input.clientOrganizationId ?? input.clientOrgId ?? '';

const toServiceError = (error: unknown): CAServiceError => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message =
      (error.response?.data as { message?: string; error?: { message?: string } } | undefined)?.error?.message ??
      (error.response?.data as { message?: string } | undefined)?.message ??
      error.message;
    if (status === 403) return new CAServiceError('FORBIDDEN', message, error.response?.data);
    if (status === 404) return new CAServiceError('NOT_FOUND', message, error.response?.data);
    if (status === 400 || status === 422) return new CAServiceError('VALIDATION_ERROR', message, error.response?.data);
    return new CAServiceError('NETWORK_ERROR', message, error.response?.data);
  }
  return new CAServiceError('UNKNOWN_ERROR', error instanceof Error ? error.message : 'Unknown CA service error', error);
};

const normalizePaginated = <T>(data: RawPaginated, mapItem: (item: unknown) => T) => {
  const items = (data.items ?? []).map(mapItem);
  return {
    items,
    total: typeof data.total === 'number' ? data.total : items.length,
    ...(typeof data.page === 'number' ? { page: data.page } : {}),
    ...(typeof data.limit === 'number' ? { limit: data.limit } : {}),
  };
};

export const caServiceHttp: ICAService = {
  async getCAClients(params) {
    try {
      const response = await api.get('/ca/clients', {
        params: {
          firmId: params.firmId,
          status: params.status,
          search: params.search,
        },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeCAClientListItem);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async createClient(payload) {
    try {
      const response = await api.post('/ca/clients', payload);
      return normalizeCAClientListItem(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async inviteClient(payload) {
    try {
      const response = await api.post('/ca/clients/invite', payload);
      return unwrapData(response.data);
    } catch (error) {
      // Backward compatibility: older backend contracts may reject inviteTarget.
      if (payload.inviteTarget && axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 400 || status === 422) {
          try {
            const legacyPayload = { ...payload };
            delete legacyPayload.inviteTarget;
            const retryResponse = await api.post('/ca/clients/invite', legacyPayload);
            return unwrapData(retryResponse.data);
          } catch (retryError) {
            throw toServiceError(retryError);
          }
        }
      }
      throw toServiceError(error);
    }
  },
  async revokeClientInvite(payload) {
    try {
      const response = await api.post(`/ca/client-links/${payload.inviteId}/revoke`);
      return unwrapData<RevokeClientInviteResponse>(response.data);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getStaff(params) {
    try {
      const response = await api.get('/ca/staff', { params: { firmId: params.firmId } });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeStaffMember);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async createStaff(payload) {
    try {
      const response = await api.post('/ca/staff', payload);
      return normalizeStaffMember(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async acceptInvite(payload) {
    try {
      const response = await api.post('/ca/clients/accept-invite', payload);
      const data = unwrapData<{ mapping?: unknown }>(response.data);
      return {
        mapping: normalizeMapping(data.mapping),
      };
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getClientDetails(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get(`/ca/clients/${encodeURIComponent(clientOrganizationId)}`, {
        params: { mappingId: params.mappingId },
      });
      return normalizeCAClientDetail(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getTasks(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/tasks', {
        params: {
          mappingId: params.mappingId,
          clientOrgId: clientOrganizationId,
          status: params.status,
        },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeTask);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async createTask(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.post('/ca/tasks', {
        ...payload,
        clientOrgId: clientOrganizationId,
      });
      const data = unwrapData<{ task?: unknown }>(response.data);
      return {
        task: normalizeTask(data.task),
      };
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getDocumentRequests(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/document-requests', {
        params: {
          mappingId: params.mappingId,
          clientOrgId: clientOrganizationId,
        },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeDocumentRequest);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async createDocumentRequest(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.post('/ca/document-requests', {
        ...payload,
        clientOrgId: clientOrganizationId,
      });
      return normalizeDocumentRequest(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async initDocumentUpload(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.post(
        `/ca/document-requests/${encodeURIComponent(payload.documentRequestId)}/upload/init`,
        {
          ...payload,
          clientOrgId: clientOrganizationId,
        }
      );
      return unwrapData(response.data);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async completeDocumentUpload(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.post(
        `/ca/document-requests/${encodeURIComponent(payload.documentRequestId)}/upload/complete`,
        {
          ...payload,
          clientOrgId: clientOrganizationId,
        }
      );
      return normalizeDocumentRequest(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async updateClientStage(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.patch('/ca/clients/stage', {
        ...payload,
        clientOrgId: clientOrganizationId,
      });
      const data = unwrapData<{ engagement?: unknown }>(response.data);
      return { engagement: normalizeEngagementState(data.engagement) };
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getAssignments(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/assignments', {
        params: { mappingId: params.mappingId, clientOrgId: clientOrganizationId },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeAssignment);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async updateAssignment(payload) {
    try {
      const response = await api.patch(`/ca/assignments/${encodeURIComponent(payload.assignmentId)}`, payload);
      return normalizeAssignment(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async updateDocumentLifecycle(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.patch(`/ca/document-requests/${encodeURIComponent(payload.documentRequestId)}`, {
        ...payload,
        clientOrgId: clientOrganizationId,
      });
      return normalizeDocumentRequest(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async bulkDocumentAction(payload) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(payload);
      const response = await api.post('/ca/document-requests/bulk', {
        ...payload,
        clientOrgId: clientOrganizationId,
      });
      return unwrapData<{ processed: number }>(response.data);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getDocumentFolders(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/documents/folders', {
        params: {
          mappingId: params.mappingId,
          clientOrgId: clientOrganizationId,
          search: params.search,
          docType: params.docType,
          owner: params.owner,
        },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeDocumentFolderNode);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getDocumentWorkflow(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/documents/workflow', {
        params: { mappingId: params.mappingId, clientOrgId: clientOrganizationId },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeDocumentWorkflowRequirement);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getAuditLog(params) {
    try {
      const clientOrganizationId = resolveClientOrganizationId(params);
      const response = await api.get('/ca/audit-log', {
        params: { mappingId: params.mappingId, clientOrgId: clientOrganizationId },
      });
      const data = unwrapData<RawPaginated>(response.data);
      return normalizePaginated(data, normalizeAuditLogEvent);
    } catch (error) {
      throw toServiceError(error);
    }
  },
  async getDashboardAnalytics(params) {
    try {
      const response = await api.get('/ca/dashboard/analytics', { params: { firmId: params.firmId } });
      return normalizeDashboardAnalytics(unwrapData(response.data));
    } catch (error) {
      throw toServiceError(error);
    }
  },
};
